<script lang="ts" setup>
import { DocumentAdd, Download, Close } from "@element-plus/icons-vue";
import { ElNotification, UploadProps, ElMessageBox } from "element-plus";
import type { UploadFile } from "element-plus";
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  reactive,
} from "vue";
import { Attachment } from "~/types/attachment";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { FormRuleType } from "~/types/element-ui/FormRuleType";
import { FormSizeType } from "~/types/element-ui/FormSizeType";
import { useProductStore } from "~/stores/productStore";
import { useInboxStore } from "~/projects/help-desk/stores/inboxStore";
import { useConfigStore } from "~/stores/configStore";
import { PermissionNames } from "~/configs/permission_names";
import { useCustomerProfileStore } from "~/stores/customerProfileStore";
import { useDebouncedSearch } from "~/composable/useDebouncedSearch";
import { useSettingsStore } from "~/stores/settingsStore";
import { usePartnerStore } from "~/projects/crm/stores/partnerStore";
import { useLeadsStore } from "~/projects/crm/stores/leadsStore";
import { useOrganizationStore } from "~/projects/crm/stores/organizationStore";
import { useChangeHistoriesStore } from "~/stores/historiesStore";
import { useLeadsPersistProfile } from "~/projects/crm/stores/leadsPersistProfile";
import {
  Lead,
  LeadProspect,
  LeadStatus,
  Organization,
  LeadSource,
} from "~/projects/crm/types";
import { LeadTimePeriod } from "~/projects/crm/types/enums/leadTimePeriod";
import { LeadView } from "~/projects/crm/types/enums/leadView";
import { LeadAbleType } from "~/projects/crm/types/enums/leadAbleType";
import { useAuthStore } from "~/stores/auth";
import { LeadCustomField } from "~/types/leadCustomField";

definePageMeta({
  layout: false,
});
const props = defineProps({
  showHeader: {
    type: Boolean,
    required: false,
    default: true,
  },
  profileId: {
    required: false,
  },
  profileName: {
    required: false,
  },
  duplicateLead: {
    type: Object,
    required: false,
    default: null,
  },
  // When true, the form runs in "bulk create from profiles" mode: the profile
  // selection is hidden, the product is prefilled & locked, a "Previous" button
  // is shown, and "Create" calls the bulk endpoint instead of the single-lead one.
  isBulkFromProfiles: {
    type: Boolean,
    required: false,
    default: false,
  },
  bulkProductId: {
    type: Number,
    required: false,
    default: null,
  },
  // Category of the bulk product, resolved on the preview modal. May be null
  // when the product has no category. Prefilled & locked like the product.
  bulkProductCategoryId: {
    type: Number,
    required: false,
    default: null,
  },
  bulkDuplicateHandling: {
    type: String,
    required: false,
    default: "",
  },
  bulkFilters: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});

const emit = defineEmits(["previous", "bulk-created", "created"]);

const configStore = useConfigStore();
const leadsStore = useLeadsStore();
const productStore = useProductStore();
const organizationStore = useOrganizationStore();
const inboxStore = useInboxStore();
const profileStore = useCustomerProfileStore();
const settingsStore = useSettingsStore();
const partnerStore = usePartnerStore();
const profileHistoriesStore = useChangeHistoriesStore();
const authStore = useAuthStore();
const leadsPersistProfile = useLeadsPersistProfile();

onMounted(async () => {
  if (props.profileName) {
    formData.name = props.profileName;
  }

  // In bulk-from-profiles mode the product is chosen on the preview modal and
  // arrives here prefilled & locked. Its category (which may be null when the
  // product has none) is carried alongside and shown locked too.
  if (props.isBulkFromProfiles && props.bulkProductId) {
    formData.product_id = props.bulkProductId as number;
    formData.product_category_id = props.bulkProductCategoryId as number | null;
  }

  // Handle duplicate lead data
  if (props.duplicateLead) {
    Object.assign(formData, props.duplicateLead);
  }

  profileStore.setSimpleFilter({});
  await organizationStore.getSimpleOrganizations();
  await profileStore.getSimpleProfiles();
  // Newest products first, so a product just created from this form is at the
  // top of the dropdown instead of buried at the bottom.
  await productStore.getAllProducts("desc");
  await settingsStore.getCrmSettings();
  await partnerStore.getSimplePartners();
  await settingsStore.getLeadCustomFields();
  if (props.profileId) {
    const profile = await profileStore.getProfile(props.profileId as number);
    profileStore.simpleProfiles.push(profile);
  }
});

const fileList = ref<UploadFile[]>([]);

const showViewer = ref(false);
const previewIndex = ref(0);

// A lead made from a ticket takes its profile from the ticket's customer,
// server-side. Offering a picker here would only look like a choice.
const isFromTicket = computed(() => leadsStore.isFromTicket);

const hasProfileId = computed(() => {
  return (
    props.profileId !== undefined &&
    props.profileId !== null &&
    props.profileId !== ""
  );
});

const getStatusIdFromLeadName = function (name) {
  return (
    configStore.globalConfig.lead_statuses.find((p) => p.name == name)?.id ??
    null
  );
};

const userFilteredStatuses = computed(() => {
  const { globalConfig } = configStore;
  const mappings = globalConfig.lead_status_user || [];
  const allStatuses = globalConfig.lead_statuses || [];

  if (!mappings.length) return allStatuses;

  const userId = authStore.user?.id;
  if (!userId) return [];

  const allowedStatusIds = new Set(
    mappings.filter((m) => m.user_id === userId).map((m) => m.status_id),
  );

  if (!allowedStatusIds.size) return [];

  return allStatuses.filter((s) => allowedStatusIds.has(s.id));
});

// When opened with an explicit status (e.g. from the board's status column)
// use it; otherwise default to the first selectable status instead of null.
const resolveInitialStatusId = () => {
  return (
    getStatusIdFromLeadName(leadsStore.createLeadStatus) ??
    userFilteredStatuses.value[0]?.id ??
    null
  );
};

const leadCount = ref<number | null>(null);
const loadingLeadCount = ref(false);

const crmLeadType = computed(() => settingsStore.crmSettings?.lead_type);

const formRef = ref<ElementUIFormRef | null>(null);
const formData = reactive<Partial<Lead>>({
  status_id: resolveInitialStatusId(),
  prospect: LeadProspect.RATING_WARM,
  assigned_users: [{ id: null, type: undefined }],
  profile_id: props?.profileId,
  profile: {
    name: "",
    phone: "",
    email: "",
  },
  custom_fields: {},
} as Partial<Lead>);

// The opening comment box shown under Description. It is not posted on its own
// — the lead payload carries it — so its content is read from here at submit
// time instead of arriving through the composer's own submit event.
const commentComposer = ref();

// Flat list of lead custom field definitions coming from /settings.
const customFields = computed<LeadCustomField[]>(
  () => settingsStore.leadCustomFields || [],
);

// Values are stored under `custom_fields`, keyed by slug, to match the backend
// payload. Ensure the object (and every slug) exists so v-model has a reactive
// target, defaulting multi-select fields to an array.
const ensureCustomFields = () => {
  if (!formData.custom_fields) {
    formData.custom_fields = {};
  }
  for (const field of customFields.value) {
    if (formData.custom_fields[field.slug] === undefined) {
      formData.custom_fields[field.slug] =
        field.type === "multi_select" ? [] : null;
    }
  }
};
watch(customFields, ensureCustomFields, { immediate: true });

const size: FormSizeType = "small";
const timePeriodOptions = [
  {
    value: LeadTimePeriod.ANNUALLY,
    label: "Annually",
  },
  {
    value: LeadTimePeriod.QUARTERLY,
    label: "Quarterly",
  },
  {
    value: LeadTimePeriod.MONTHLY,
    label: "Monthly",
  },
  {
    value: LeadTimePeriod.PERPETUAL,
    label: "Perpetual",
  },
];
const attachmentArray = ref([] as Attachment[]);
const attachmentUrl = ref("");
const showAttachmentModal = ref(false);
const showFollowUpForm = ref(false);

type FollowUpTaskForm = {
  title: string | number | null;
  follow_up_at: string | null;
  agent_id: number | null;
};

const followUpTaskForms = ref<FollowUpTaskForm[]>([]);

const shortcuts = [
  {
    text: "Today",
    value: new Date(),
  },
  {
    text: "Tomorrow",
    value: () => {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      return date;
    },
  },
  {
    text: "In 7 days",
    value: () => {
      const date = new Date();
      date.setDate(date.getDate() + 7);
      return date;
    },
  },
];

const disabledDate = (time: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return time.getTime() < today.getTime();
};

const handleDueDateChange = () => {
  // Keep handler for future business logic (e.g. SLA checks / reminders).
};
const rules = reactive<FormRuleType>({
  name: [
    {
      required: true,
      message: "title is required",
      trigger: "blur",
    },
  ],
  assigned_users: [
    {
      required: true,
      validator: (_, value, callback) => {
        if (!value || value.length === 0 || value.some((user) => !user.id)) {
          callback(new Error("Select valid agent"));
        } else {
          callback();
        }
      },
      trigger: "change",
    },
  ],
  profile_id: [
    {
      required: false,
      trigger: "change",
    },
  ],
  status_id: [
    {
      required: true,
      message: "status is required",
      trigger: "change",
    },
  ],

  org_category_id: [
    {
      required: false,
      trigger: "change",
    },
  ],
  organization_id: [
    {
      required: false,
      trigger: "change",
    },
  ],
  partner_id: [
    {
      required: false,
      trigger: "change",
    },
  ],
  product_category_id: [
    {
      required: false,
      trigger: "change",
    },
  ],
  product_id: [
    {
      required: true,
      message: "product is required",
      trigger: "change",
    },
  ],
});

const activeTab = ref("basic");

const appendAttachment = (value: Attachment) => {
  attachmentArray.value.push(value);
};

const openAttachment = (url: string) => {
  attachmentUrl.value = url;
  window.open(attachmentUrl.value as unknown as string, "_blank");
};

const handleAttachmentOpen = function () {
  showAttachmentModal.value = true;
};

const closeAttachment = () => {
  showAttachmentModal.value = false;
};

const openFollowUpForm = async () => {
  showFollowUpForm.value = true;

  const selectedAgentIds = [
    ...new Set(
      (formData.assigned_users || [])
        .map((u) => u.id)
        .filter((id): id is number => Boolean(id)),
    ),
  ];

  followUpTaskForms.value = selectedAgentIds.length
    ? selectedAgentIds.map((agentId) => ({
        title: null,
        follow_up_at: null,
        agent_id: agentId,
      }))
    : [
        {
          title: null,
          follow_up_at: null,
          agent_id: null,
        },
      ];

  const newFollowUpIndex = followUpTaskForms.value.length - 1;

  await nextTick();

  const followUpElement = document.getElementById(
    `follow-up-item-${newFollowUpIndex}`,
  );

  followUpElement?.scrollIntoView({
    behavior: "smooth",
    block: "nearest",
  });
};

const removeFollowUp = (index: number) => {
  followUpTaskForms.value.splice(index, 1);

  if (!followUpTaskForms.value.length) {
    showFollowUpForm.value = false;
  }
};

const validateFollowUps = () => {
  if (!showFollowUpForm.value) return true;

  if (!followUpTaskForms.value.length) {
    ElNotification({
      title: "Failed",
      message: "Please add at least one follow up",
      type: "error",
    });
    return false;
  }

  const hasInvalidFollowUp = followUpTaskForms.value.some((followUp) => {
    const hasTitle =
      followUp.title !== null && `${followUp.title}`.trim() !== "";
    const hasDueDate = !!followUp.follow_up_at;
    const hasAgent = !!followUp.agent_id;

    return !(hasTitle && hasDueDate && hasAgent);
  });

  if (hasInvalidFollowUp) {
    ElNotification({
      title: "Failed",
      message:
        "Follow up title, due date & time, and at least one agent are required",
      type: "error",
    });
    return false;
  }

  return true;
};

const normalizePhone = (raw?: string | null): string => {
  let phone = String(raw ?? "").replace(/[\s\-_.()]/g, "");

  if (phone.startsWith("+")) {
    phone = phone.slice(1);
  } else if (phone.startsWith("00")) {
    phone = phone.slice(2);
  }

  return phone;
};

const phoneMatchedProfile = ref<Record<string, any> | null>(null);
const phoneLeadCount = ref<number | null>(null);
const checkingPhone = ref(false);

const phoneCheckState = ref<"idle" | "matched" | "none">("idle");
// Bumped per lookup so a slow earlier response cannot overwrite a newer one.
let phoneLookupToken = 0;

let lastLookedUpPhone: string | null = null;

const isNewProfileMode = computed(
  () =>
    leadsPersistProfile.showLeadProfile &&
    !hasProfileId.value &&
    !isFromTicket.value,
);

const activeLeadCount = computed(() =>
  isNewProfileMode.value ? phoneLeadCount.value : leadCount.value,
);

const clearPhoneMatch = () => {
  phoneLookupToken++;
  phoneMatchedProfile.value = null;
  phoneLeadCount.value = null;
  phoneCheckState.value = "idle";
  lastLookedUpPhone = null;
};

const normalizedPhone = computed(() => normalizePhone(formData.profile?.phone));

// Digits only once normalized. This also keeps half-typed junk out of the URL
// path the endpoint reads its parameter from.
const canCheckPhone = computed(() => /^[0-9]+$/.test(normalizedPhone.value));

const lookupProfileByPhone = async (rawPhone?: string | null) => {
  const phone = normalizePhone(rawPhone);
  const token = ++phoneLookupToken;

  if (!/^[0-9]+$/.test(phone)) {
    phoneMatchedProfile.value = null;
    phoneLeadCount.value = null;
    phoneCheckState.value = "idle";
    lastLookedUpPhone = null;
    return;
  }

  // Drop the previous phone's answer before going to fetch this one, so the
  // badge never sits there describing a number the agent has already edited.
  if (phone !== lastLookedUpPhone) {
    phoneMatchedProfile.value = null;
    phoneLeadCount.value = null;
  }

  const profile = await profileStore.getProfileByPhone(phone);

  if (token !== phoneLookupToken) return;

  // The store returns undefined when the request itself failed and null when it
  // succeeded with no match. Only a real answer is worth remembering: recording
  // a failure as "looked up" would let it stand in for "no existing profile"
  // and quietly suppress the warning on submit.
  if (profile === undefined) {
    phoneMatchedProfile.value = null;
    phoneLeadCount.value = null;
    phoneCheckState.value = "idle";
    return;
  }

  if (!profile?.id) {
    phoneMatchedProfile.value = null;
    phoneLeadCount.value = null;
    phoneCheckState.value = "none";
    lastLookedUpPhone = phone;
    return;
  }

  // getProfileHistories leaves the last count in place when its request fails,
  // so clear it first rather than reading back another profile's number.
  profileHistoriesStore._profileHistoryCount = null;

  // Same endpoint the picker uses, so both modes report the same number for the
  // same profile - including under hierarchical org structures, where it counts
  // only the leads assigned to the user's subordinates.
  await profileHistoriesStore.getProfileHistories(
    profile.id,
    "lead",
    null,
    "count",
  );

  if (token !== phoneLookupToken) return;

  phoneMatchedProfile.value = profile;
  phoneLeadCount.value = profileHistoriesStore.profileHistoryCount as
    | number
    | null;
  phoneCheckState.value = "matched";

  // Recorded only now that the count is actually in. Marking the phone as
  // resolved any earlier would let a Create clicked mid-flight skip the
  // submit-time re-check below and warn off a count that had not arrived.
  lastLookedUpPhone = phone;
};

// Checking is on demand, so this only ever CLEARS - it never goes to the network.
// Editing the phone after a check invalidates the answer on screen, and leaving a
// badge up that describes a number the agent has since changed would be worse
// than showing nothing.
watch(
  () => formData.profile?.phone,
  (phone) => {
    if (!isNewProfileMode.value) return;

    if (normalizePhone(phone) !== lastLookedUpPhone) {
      clearPhoneMatch();
    }
  },
);

// Toggling between the two modes drops whatever the phone had resolved to; the
// picker's own watcher below keeps `leadCount` in step the other way.
watch(isNewProfileMode, () => {
  clearPhoneMatch();
});

onBeforeUnmount(() => {
  // Parks any in-flight response, which would otherwise still be written back
  // into refs belonging to a form the agent has closed.
  phoneLookupToken++;
});

// The "Check Leads" button. The same lookup Create runs for itself, just invoked
// deliberately so the agent can see the answer before committing to it.
const checkPhoneForLeads = async () => {
  if (!canCheckPhone.value || checkingPhone.value) return;

  const phone = normalizedPhone.value;

  checkingPhone.value = true;
  await lookupProfileByPhone(phone);
  checkingPhone.value = false;

  // Still "idle" means the lookup produced no answer at all - the request
  // failed. Editing the phone mid-check also lands here, via the watcher below
  // superseding this lookup, so only speak up while the field still holds the
  // number that was checked.
  if (phone === normalizedPhone.value && phoneCheckState.value === "idle") {
    ElNotification({
      title: "Could not check",
      message: "Please try checking this phone number again",
      type: "error",
    });
  }
};

const fetchLeadCount = async (profileId: number) => {
  if (!profileId) {
    leadCount.value = null;
    return;
  }

  loadingLeadCount.value = true;

  await profileHistoriesStore.getProfileHistories(
    profileId,
    "lead",
    null,
    "count",
  );

  leadCount.value = profileHistoriesStore.profileHistoryCount;

  loadingLeadCount.value = false;
};
watch(
  () => formData.profile_id,
  async (newProfileId) => {
    if (newProfileId) {
      await fetchLeadCount(newProfileId);
    } else {
      leadCount.value = null;
    }
  },
  { immediate: true },
);

const createLeadWithWarning = async function () {
  if (!formRef.value) return;

  // Checking is manual, so the agent may never have pressed the button - or may
  // have edited the phone since. Create still has to warn, so settle the lookup
  // here whenever the field has moved on from what was last resolved.
  if (isNewProfileMode.value && normalizedPhone.value !== lastLookedUpPhone) {
    await lookupProfileByPhone(normalizedPhone.value);
  }

  if (activeLeadCount.value && activeLeadCount.value > 0) {
    try {
      await ElMessageBox.confirm(
        `This profile already has ${activeLeadCount.value} lead(s). Do you want to create another?`,
        "Warning",
        {
          confirmButtonText: "Yes",
          cancelButtonText: "No",
          type: "warning",
        },
      );
      await createLead();
    } catch {}
  } else {
    await createLead();
  }
};

const createLead = function () {
  if (!formRef.value) return;
  formRef.value?.validate(async (valid: any) => {
    if (!valid) {
      ElNotification({
        title: "Failed",
        message: "Please provide all the required fields",
        type: "error",
      });
    } else if (valid) {
      if (!validateFollowUps()) {
        return;
      }

      formData.unique_identifiers = attachmentArray.value.map(
        (attachment) => attachment.unique_identifier,
      );

      const isUsingNewProfile =
        leadsPersistProfile.showLeadProfile && !hasProfileId.value;

      // Normalized to match what "Check Leads" looked up. Sending the raw text
      // instead would store "(017)11111111" for a phone the check had reported
      // on as "01711111111", leaving the new profile invisible to the next check.
      const profilePayload = {
        name: formData.profile?.name,
        phone: normalizePhone(formData.profile?.phone),
        email: formData.profile?.email,
      };

      const hasRequiredProfileValue =
        String(profilePayload.name ?? "").trim() !== "" &&
        String(profilePayload.phone ?? "").trim() !== "";

      // remove profile before spreading formData
      const { profile, ...formDataWithoutProfile } = formData;

      // null when the box was left empty, in which case no comment key is sent.
      const comment = commentComposer.value?.getContent();

      // Custom field values already live on formData.custom_fields (keyed by
      // slug) and the backend expects them nested under `custom_fields`, so
      // they are carried through via ...formDataWithoutProfile.
      const payload = {
        ...formDataWithoutProfile,

        ...(isUsingNewProfile
          ? {
              ...(hasRequiredProfileValue
                ? {
                    profile: profilePayload,
                  }
                : {}),
              profile_id: null,
            }
          : {
              profile_id: formData.profile_id,
            }),

        ...(showFollowUpForm.value
          ? {
              follow_ups: followUpTaskForms.value.map((followUp) => ({
                title: followUp.title,
                follow_up_at: followUp.follow_up_at,
                agent_id: followUp.agent_id ?? null,
              })),
            }
          : {}),

        ...(comment
          ? {
              comment: {
                body: comment.body,
                mentioned_users_id: comment.mentionedIds,
              },
            }
          : {}),
      };

      if (payload.profile_id) {
        delete payload.profile;
      }

      const missingName = fileList.value.some(
        (file) => !file.attachmentName?.trim(),
      );
      if (missingName) {
        ElNotification({
          title: "Name is required",
          message: "Please provide a name for each attachment.",
          type: "error",
        });
        return;
      }
      const attachments = fileList.value.map((file) => ({
        file: file.raw,
        name: file.attachmentName,
        description: file.description || "",
      }));

      const lead = await leadsStore.createLead(payload as Lead, attachments);

      if (!lead) {
        return;
      }

      if (lead) {
        ElNotification({
          title: "Lead created",
          message: "New lead created",
          type: "success",
        });
      }
      leadsStore.view === LeadView.TABLE
        ? leadsStore.getLeads()
        : leadsStore.getStatusLeads();
      leadsStore.closeLeadCreateModal();
      leadsStore.setLeadCreateDialogVisibility(false);
      if (props.duplicateLead) {
        leadsStore.setDuplicateLeadData(null);
        leadsStore.setLeadDuplicateDialogVisibility(false);
      }
      if (leadsStore._leadableData?.leadable_type === LeadAbleType.ticket) {
        inboxStore.setSelectedTicket(leadsStore._leadableData?.leadable_id);
      }
      // createLead returns the axios response; the new lead is in response.data.
      emit("created", (lead as any)?.data?.id ?? null);
    } else {
      console.log("error in validation");
    }
  });
};

// Dispatch the submit to the right handler depending on the mode.
const handleSubmit = () => {
  if (props.isBulkFromProfiles) {
    createBulkLeads();
  } else {
    createLeadWithWarning();
  }
};

const createBulkLeads = function () {
  if (!formRef.value) return;
  formRef.value?.validate(async (valid: any) => {
    if (!valid) {
      ElNotification({
        title: "Failed",
        message: "Please provide all the required fields",
        type: "error",
      });
      return;
    }

    if (!validateFollowUps()) {
      return;
    }

    formData.unique_identifiers = attachmentArray.value.map(
      (attachment) => attachment.unique_identifier,
    );

    // Bulk creation targets the filtered profile set, so the per-lead profile
    // fields are dropped; the product / duplicate-handling / filters context is
    // passed separately and merged into the request by the store action.
    const leadPayload: Record<string, any> = { ...formData };
    delete leadPayload.profile;
    delete leadPayload.profile_id;

    if (showFollowUpForm.value) {
      leadPayload.follow_ups = followUpTaskForms.value.map((followUp) => ({
        title: followUp.title,
        follow_up_at: followUp.follow_up_at,
        agent_id: followUp.agent_id ?? null,
      }));
    }

    const result = await profileStore.createBulkLeadFromProfiles(leadPayload, {
      product_id: props.bulkProductId as number,
      duplicate_handling: props.bulkDuplicateHandling,
      filters: props.bulkFilters ?? {},
    });

    if (!result) {
      return;
    }

    ElNotification({
      title: "Leads created",
      message: result.message || "Bulk leads created successfully",
      type: "success",
    });

    emit("bulk-created");
  });
};

const goToPrevious = () => {
  emit("previous");
};

const closeModal = () => {
  ElNotification({
    type: "warning",
    title: "Cancelled",
  });
  leadsStore.createLeadStatus = null;
  leadsStore.setLeadCreateDialogVisibility(false);
  leadsStore.closeLeadCreateModal();
  if (props.duplicateLead) {
    leadsStore.setDuplicateLeadData(null);
    leadsStore.setLeadDuplicateDialogVisibility(false);
  }

  console.log(
    leadsStore.createLeadStatus,
    "createLeadStatus in onMounted of CreateForm",
  );
};

const onChangeOrgCategory = async () => {
  if (formData.org_category_id) {
    formData.organization_id = null;
    organizationStore.setSimpleFilter({
      category_id: formData.org_category_id,
    });
    await organizationStore.getSimpleOrganizations();
  }
};
const onChangeProductCategory = async () => {
  if (formData.product_category_id) {
    formData.product_id = null;
    productStore.setSimpleFilter({
      category_id: formData.product_category_id,
    });
    await productStore.getAllProducts("desc");
  }
};

const onChangeOrganization = async () => {
  formData.profile_id = null;

  if (formData.organization_id) {
    profileStore.setSimpleFilter({
      organization_id: formData.organization_id,
    });
  } else {
    profileStore.setSimpleFilter({});
  }
  await profileStore.getSimpleProfiles();
};
const onChangeProfile = async () => {
  if (formData.organization_id) {
    profileStore.setSimpleFilter({
      organization_id: formData.organization_id,
    });
  } else {
    profileStore.setSimpleFilter({});
  }
  await profileStore.getSimpleProfiles();
};

const onChangePartner = async () => {
  if (formData.partner_id) {
    partnerStore.setSimpleFilter({ id: formData.partner_id });
  } else {
    partnerStore.setSimpleFilter({});
  }
  await partnerStore.getSimplePartners();
};

const { remoteMethod, loading } = useDebouncedSearch((query) => {
  profileStore._simpleFilter.search = query;
  profileStore.getSimpleProfiles();
});

const handleUserSelection = () => {
  formData.assigned_users = formData.assigned_users?.map((user) => ({
    id: user.id,
    type: user.type || undefined,
  }));
};

const assignUser = () => {
  formData.assigned_users?.push({ id: null, type: undefined });
};
const removeUser = (index: number) => {
  formData.assigned_users?.splice(index, 1);
};

// "Search User by Branch" deliberately starts empty, so the agent list opens
// with everyone the user may assign - the same set the edit form shows.
//
// It used to default to the creator's own branch, which silently narrowed the
// list to that branch and, because users.branch_id is nullable and the filter
// below compares with ===, dropped every agent who has no branch at all. The
// value is never persisted (leads have no branch_id column; the backend
// discards it), so it only ever served to filter this dropdown.

const filteredAgents = computed(() => {
  const { isOrgStructureHierarchical, globalConfig } = configStore;
  const branchId = formData.branch_id;

  const agents = isOrgStructureHierarchical
    ? globalConfig.subordinates
    : globalConfig.agents;

  return branchId
    ? agents.filter((agent) => agent.branch_id === branchId)
    : agents;
});

const statusFilteredAgents = computed(() => {
  const { globalConfig } = configStore;

  const baseAgents = filteredAgents.value;

  const mappings = globalConfig.lead_status_user || [];

  if (!mappings.length) return baseAgents;

  const statusId = formData.status_id;
  if (!statusId) return [];

  const allowedUserIds = new Set(
    mappings.filter((m) => m.status_id === statusId).map((m) => m.user_id),
  );

  if (!allowedUserIds.size) return [];

  return baseAgents.filter((agent) => allowedUserIds.has(agent.id));
});

watch(
  statusFilteredAgents,
  (agents) => {
    const userId = authStore.user?.id;

    if (!userId || !agents.length) return;

    const currentUser = agents.find((agent) => agent.id === userId);

    if (currentUser && formData.assigned_users?.length) {
      formData.assigned_users[0].id = currentUser.id;
    }
  },
  { immediate: true },
);

const handleStageChange = (newStatusId: number | null) => {
  const mappings = configStore.globalConfig.lead_status_user || [];

  // If no mapping exists, allow change freely
  if (!mappings.length) {
    formData.status_id = newStatusId;
    return;
  }

  // If cleared
  if (!newStatusId) {
    formData.status_id = newStatusId;
    return;
  }

  const allowedUserIds = new Set(
    mappings.filter((m) => m.status_id === newStatusId).map((m) => m.user_id),
  );

  const allAgents = filteredAgents.value;
  const allStatuses = configStore.globalConfig.lead_statuses || [];

  const attemptedStage = allStatuses.find((s) => s.id === newStatusId);

  const invalidAssignedUsers = formData.assigned_users.filter(
    (assignedUser) => {
      if (!assignedUser.id) return false;
      return !allowedUserIds.has(assignedUser.id);
    },
  );

  if (invalidAssignedUsers.length) {
    const bulletList = invalidAssignedUsers
      .map((assignedUser) => {
        const matchedAgent = allAgents.find(
          (agent) => agent.id === assignedUser.id,
        );
        return `• ${matchedAgent?.name || `User ${assignedUser.id}`}`;
      })
      .join("<br>");

    ElMessageBox({
      type: "warning",
      dangerouslyUseHTMLString: true,
      message: `Following Agent(s) ${
        invalidAssignedUsers.length > 1 ? "are" : "is"
      } not in the ${attemptedStage?.name || "selected"} stage.<br><br>
      ${bulletList}<br><br>
      Please either remove ${
        invalidAssignedUsers.length > 1 ? "them" : "this user"
      } from the lead or assign ${
        invalidAssignedUsers.length > 1 ? "them" : "this user"
      } in the ${attemptedStage?.name || "selected"} stage.`,
    });

    return;
  }

  formData.status_id = newStatusId;
};

// A lead opened from a ticket carries the ad/shortlink the customer came in
// through. The inbox resolves which one (the ticket's own referral, else the
// newest info conversation's) and the store sends the id with the payload, so
// this is shown read-only — there is nothing for the agent to pick.
const leadReferral = computed(() => leadsStore._leadableData?.referral ?? null);

const leadReferralLabel = computed(() => {
  const referral = leadReferral.value;

  if (!referral) return "";

  return referral.title || `Referral #${referral.id}`;
});

const isLeadTitleManuallyEdited = ref(false);

const selectedProfileName = computed(() => {
  if (leadsPersistProfile.showLeadProfile && !hasProfileId.value) {
    return formData.profile?.name || "";
  }

  const selectedProfile = profileStore.simpleProfiles.find(
    (profile) => profile.id === formData.profile_id,
  );

  return selectedProfile?.name || "";
});

const autoLeadTitle = computed(() => {
  // Bulk mode creates one lead per matching profile, so the title is a fixed
  // template the backend fills in per profile — see
  // ProfileBulkLeadService::buildLeadName(), which still appends the product.
  if (props.isBulkFromProfiles) {
    return "{Profile Name} || {Product Name}";
  }

  // Profile name only. The product used to be appended here as
  // "{profile} || {product}", so picking or changing a product rewrote a title
  // the agent had not touched.
  return selectedProfileName.value;
});

watch(
  autoLeadTitle,
  (value) => {
    if (!isLeadTitleManuallyEdited.value) {
      formData.name = value;
    }
  },
  { immediate: true },
);

const handleLeadTitleInput = () => {
  isLeadTitleManuallyEdited.value = true;
};
const handleFileChange: UploadProps["onChange"] = (uploadFile) => {
  const exists = fileList.value.find((file) => file.uid === uploadFile.uid);

  if (!uploadFile.url && uploadFile.raw?.type.startsWith("image")) {
    uploadFile.url = URL.createObjectURL(uploadFile.raw);
  }
  if (!uploadFile.attachmentName) {
    uploadFile.attachmentName = uploadFile.name
      .replace(/\.[^/.]+$/, "")
      .split(" ")[0];
  }

  if (!exists) {
    fileList.value.push({
      ...uploadFile,
      attachmentName: uploadFile.name,
      description: "",
    });
  }
};
const removeFile = (index: number) => {
  const file = fileList.value[index];
  if (file.url) {
    URL.revokeObjectURL(file.url);
  } else if (file.raw) {
    URL.revokeObjectURL(URL.createObjectURL(file.raw));
  }
  fileList.value.splice(index, 1);
};
const getAcceptValue = () => {
  const accept =
    ".jpg, .jpeg, .png, .pdf,.ppt, .pptx, .xlx, .xlxs, .doc, .docx, .wav, .heic, .mp3, .mp4";
  return accept;
};
const handlePreview: UploadProps["onPreview"] = (file) => {
  const exists = fileList.value.find((f) => f.uid === file.uid);
  if (!exists) {
    if (!file.url && file.raw?.type.startsWith("image")) {
      file.url = URL.createObjectURL(file.raw);
    }
    fileList.value.push(file);
  }
};
</script>

<template>
  <EditModalHeader v-if="props.showHeader">
    <template #title> Create Lead</template>
    <template #svg>
      <Svgs name="pen" />
    </template>
  </EditModalHeader>
  <el-form
    ref="formRef"
    :model="formData as Lead"
    class="space-y-5"
    :rules="rules"
    @submit.prevent="handleSubmit"
  >
    <!-- Tabs wrapper -->
    <div class="feature-filter-grid-1">
      <el-tabs v-model="activeTab" type="card" class="custom-basic-tabs">
        <!-- Basic Tab -->
        <el-tab-pane label="Basic" name="basic">
          <div class="grid gap-4">
            <div v-if="!isBulkFromProfiles" class="px-2">
              <!-- Personal Information Form -->

              <div v-if="isNewProfileMode">
                <div class="flex flex-col gap-3">
                  <div class="flex items-center justify-start gap-1">
                    <span class="text-light-sec-purple text-base">
                      Profile Information
                    </span>

                    <!-- The phone below already belongs to a profile, and this
                         is how many leads that profile carries. Same wording the
                         picker uses, so the two modes read alike. -->
                    <span
                      v-if="
                        phoneCheckState === 'matched' && phoneLeadCount !== null
                      "
                      class="ml-2 text-xs text-red-400"
                      :title="
                        phoneMatchedProfile?.name
                          ? `Matched existing profile: ${phoneMatchedProfile.name}`
                          : undefined
                      "
                    >
                      (This Profile has {{ phoneLeadCount }} lead(s))
                    </span>

                    <!-- A checked phone that matched nothing. Shown so pressing
                         the button always says something back. -->
                    <span
                      v-else-if="phoneCheckState === 'none'"
                      class="ml-2 text-xs text-gray-400"
                    >
                      (No existing profile with this phone)
                    </span>
                  </div>
                  <hr
                    class="border-t border-[#B7ABE8] border-dotted dark:border-dotted dark:border-dark-primary-border m-1"
                  />

                  <el-form-item class="form-elem-container" prop="profile.name">
                    <div class="form-elem-container">
                      <FormLabel :required="true">Name</FormLabel>
                      <el-input
                        v-model="formData.profile.name"
                        clearable
                        placeholder="Please input name"
                      />
                    </div>
                  </el-form-item>

                  <el-form-item
                    class="form-elem-container"
                    prop="profile.phone"
                  >
                    <div class="form-elem-container">
                      <FormLabel :required="true">Phone</FormLabel>
                      <div class="flex items-center gap-2">
                        <el-input
                          v-model="formData.profile.phone"
                          clearable
                          class="w-full"
                          placeholder="Please input phone"
                        />
                        <el-button
                          class="shrink-0 !bg-[#F4F1FF] !text-[#3B3A64]"
                          :loading="checkingPhone"
                          :disabled="!canCheckPhone"
                          :title="
                            canCheckPhone
                              ? 'Look up the profile this phone belongs to'
                              : 'Enter a phone number first'
                          "
                          @click.prevent="checkPhoneForLeads"
                        >
                          Check Leads
                        </el-button>
                      </div>
                    </div>
                  </el-form-item>

                  <el-form-item
                    class="form-elem-container"
                    prop="profile.email"
                  >
                    <div class="form-elem-container">
                      <FormLabel>Email</FormLabel>
                      <el-input
                        v-model="formData.profile.email"
                        clearable
                        placeholder="Please input email"
                      />
                    </div>
                  </el-form-item>
                </div>

                <button
                  type="button"
                  class="text-light-purple underline text-xs dark:text-light-purple-bar whitespace-nowrap mt-1"
                  @click="leadsPersistProfile.showLeadProfile = false"
                >
                  Select From Existing Profile
                </button>
              </div>

              <!-- Existing Profile Select -->
              <div v-else>
                <el-form-item class="form-elem-container" prop="profile_id">
                  <div class="form-elem-container">
                    <div class="flex items-center justify-start gap-1">
                      <FormLabel>Profile</FormLabel>

                      <span
                        v-if="isFromTicket"
                        class="ml-2 text-xs text-gray-400"
                      >
                        (taken from the ticket's customer)
                      </span>

                      <span
                        v-if="leadCount !== null"
                        class="ml-2 text-xs text-red-400"
                      >
                        (This Profile has {{ leadCount }} lead(s))
                      </span>
                    </div>
                    <div class="flex items-center mt-1 gap-2">
                      <el-select
                        v-model="formData.profile_id"
                        :disabled="isFromTicket"
                        filterable
                        clearable
                        class="w-full"
                        placeholder="Search by name, phone or email"
                        remote
                        reserve-keyword
                        :remote-method="remoteMethod"
                        :loading="loading"
                        @clear="onChangeProfile"
                      >
                        <el-option
                          v-for="profile in profileStore.simpleProfiles"
                          :key="profile.id"
                          :label="
                            profile.default_phone
                              ? `${profile.name} | ${profile.default_phone.phone}`
                              : profile.name
                          "
                          :value="profile.id"
                        ></el-option>
                      </el-select>
                      <div
                        v-if="!isFromTicket"
                        class="ml-auto !w-auto flex !justify-end"
                      >
                        <ProfileAddButton
                          :org-id="formData.organization_id"
                          :is-from-lead="true"
                        />
                      </div>
                    </div>

                    <div>
                      <button
                        v-if="!hasProfileId && !isFromTicket"
                        type="button"
                        class="text-light-purple underline text-xs dark:text-light-purple-bar whitespace-nowrap"
                        @click="leadsPersistProfile.showLeadProfile = true"
                      >
                        Show Profile Details
                      </button>
                    </div>
                  </div>
                </el-form-item>
              </div>
            </div>

            <div class="flex flex-col gap-3 px-2">
              <span
                v-if="!hasProfileId"
                class="text-light-sec-purple text-base"
              >
                Lead Information
              </span>
              <hr
                v-if="!hasProfileId"
                class="border-t border-[#B7ABE8] border-dotted dark:border-dotted dark:border-dark-primary-border m-1"
              />
              <el-form-item class="form-elem-container" prop="name">
                <div class="form-elem-container">
                  <FormLabel :required="true">Title</FormLabel>
                  <el-input
                    v-model="formData.name"
                    clearable
                    :disabled="isBulkFromProfiles"
                    placeholder="Please input title"
                    @input="handleLeadTitleInput"
                  />
                </div>
              </el-form-item>

              <el-form-item
                class="form-elem-container"
                prop="product_category_id"
              >
                <div class="form-elem-container">
                  <FormLabel>Product Category</FormLabel>
                  <el-select
                    v-model="formData.product_category_id"
                    filterable
                    clearable
                    :disabled="isBulkFromProfiles"
                    class="w-full"
                    placeholder="Please select product category"
                    @change="onChangeProductCategory"
                  >
                    <el-option
                      v-for="category in configStore.globalConfig
                        .product_categories"
                      :key="category.id"
                      :label="category.name"
                      :value="category.id"
                    ></el-option>
                  </el-select>
                </div>
              </el-form-item>
              <el-form-item class="form-elem-container" prop="product_id">
                <div class="form-elem-container">
                  <FormLabel :required="true">Product</FormLabel>
                  <div class="flex items-center mt-1 gap-2">
                    <el-select
                      v-model="formData.product_id"
                      filterable
                      class="w-full"
                      clearable
                      :disabled="isBulkFromProfiles"
                      placeholder="Please select a product"
                    >
                      <el-option
                        v-for="product in productStore.allProducts"
                        :key="product.id"
                        :label="product.name"
                        :value="product.id"
                      ></el-option>
                    </el-select>
                    <ProductsAddButton
                      v-if="!isBulkFromProfiles"
                      :product-category-id="formData.product_category_id"
                    />
                  </div>
                </div>
              </el-form-item>

              <el-form-item class="form-elem-container" prop="status_id">
                <div class="form-elem-container">
                  <FormLabel :required="true">Stage</FormLabel>
                  <el-select
                    :model-value="formData.status_id"
                    class="w-full"
                    clearable
                    placeholder="Please select stage"
                    @update:model-value="handleStageChange"
                  >
                    <el-option
                      v-for="status in userFilteredStatuses"
                      :key="status.id"
                      :label="status.name"
                      :value="status.id"
                    ></el-option>
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item class="form-elem-container" prop="branch_id">
                <div class="form-elem-container">
                  <FormLabel>Search User by Branch</FormLabel>
                  <el-select v-model="formData.branch_id" filterable clearable>
                    <el-option
                      v-for="branch in configStore.globalConfig.branches"
                      :key="branch.id"
                      :label="branch.name"
                      :value="branch.id"
                    ></el-option>
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item class="form-elem-container" prop="assigned_users">
                <div class="form-elem-container">
                  <div class="flex items-center gap-2 justify-between mt-2">
                    <FormLabel :required="true">Assigned Agents</FormLabel>
                    <UsersAddButton />
                  </div>
                  <div
                    v-for="(_, index) in formData.assigned_users"
                    :key="index"
                  >
                    <div class="flex flex-col sm:flex-row items-center">
                      <el-select
                        v-model="formData.assigned_users[index].id"
                        placeholder="Select User"
                        filterable
                        style="width: 100%"
                        @change="handleUserSelection"
                      >
                        <el-option
                          v-for="agent in statusFilteredAgents"
                          :key="agent.id"
                          :label="agent.name"
                          :value="agent.id"
                        />
                      </el-select>
                      <el-select
                        v-model="formData.assigned_users[index].type"
                        placeholder="Select Type"
                        clearable
                        filterable
                        style="width: 100%"
                      >
                        <el-option
                          v-for="type in configStore.globalConfig.crm
                            .lead_users_association_types"
                          :key="type"
                          :label="type"
                          :value="type"
                        />
                      </el-select>
                      <el-icon
                        class="ml-2 cursor-pointer"
                        @click="removeUser(index)"
                      >
                        <Close
                      /></el-icon>
                    </div>
                  </div>
                  <button
                    class="cursor-pointer text-white bg-light-purple text-xs w-fit px-2 py-2 mt-2 rounded"
                    :disabled="
                      !isUserPermitted(PermissionNames.LEAD_ASSIGN_USER)
                    "
                    :title="
                      !isUserPermitted(PermissionNames.LEAD_ASSIGN_USER)
                        ? 'You don\'t have permission'
                        : ''
                    "
                    @click.prevent="assignUser"
                  >
                    <span> Assign User </span>
                  </button>
                </div>
              </el-form-item>

              <el-form-item
                v-if="crmLeadType === 'b2b'"
                class="form-elem-container"
                prop="organization"
              >
                <div class="form-elem-container">
                  <FormLabel>Organization Category</FormLabel>
                  <el-select
                    v-model="formData.org_category_id"
                    filterable
                    clearable
                    class="w-anizationfull"
                    placeholder="Please select organization category"
                    @change="onChangeOrgCategory"
                  >
                    <el-option
                      v-for="category in configStore.globalConfig
                        .organization_categories"
                      :key="category.id"
                      :label="category.name"
                      :value="category.id"
                    ></el-option>
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item
                v-if="crmLeadType === 'b2b'"
                class="form-elem-container"
                prop="organization_id"
              >
                <div class="form-elem-container">
                  <FormLabel>Organization</FormLabel>
                  <div class="flex items-center gap-2">
                    <el-select
                      v-model="formData.organization_id"
                      filterable
                      clearable
                      class="w-full"
                      placeholder="Please select organization"
                      @change="onChangeOrganization"
                    >
                      <el-option
                        v-for="org in organizationStore.simpleOrganizations"
                        :key="org.id"
                        :label="org.name"
                        :value="org.id"
                      ></el-option>
                    </el-select>
                    <OrganizationsAddButton />
                  </div>
                </div>
              </el-form-item>

              <el-form-item
                v-if="settingsStore._crmSettings?.lead_has_partner"
                class="form-elem-container"
                prop="partner_id"
              >
                <div class="form-elem-container">
                  <FormLabel>Partner</FormLabel>
                  <div class="flex items-center gap-2">
                    <el-select
                      v-model="formData.partner_id"
                      filterable
                      class="w-full"
                      clearable
                      placeholder="Please select a partner"
                      @change="onChangePartner"
                    >
                      <el-option
                        v-for="partner in partnerStore.simplePartners"
                        :key="partner.id"
                        :label="partner.name"
                        :value="partner.id"
                      ></el-option>
                    </el-select>
                    <PartnersAddButton :partner-id="formData.partner_id" />
                  </div>
                </div>
              </el-form-item>

              <el-form-item class="form-elem-container" prop="prospect">
                <div class="form-elem-container">
                  <FormLabel>Prospect</FormLabel>
                  <el-select
                    v-model="formData.prospect"
                    class="w-full"
                    clearable
                    placeholder="Please select prospect"
                  >
                    <el-option
                      v-for="prospect in Object.values(LeadProspect)"
                      :key="prospect"
                      :label="snakeToTitleCase(prospect)"
                      :value="prospect"
                    />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item class="form-elem-container" prop="source">
                <div class="form-elem-container">
                  <FormLabel>Source</FormLabel>
                  <el-select
                    v-model="formData.source"
                    class="w-full"
                    clearable
                    placeholder="Please select source"
                  >
                    <el-option
                      v-for="source in configStore.globalConfig.crm
                        .lead_source_list"
                      :key="source"
                      :label="snakeToTitleCase(source)"
                      :value="source"
                    />
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item class="form-elem-container" prop="follow_up_date">
                <div class="form-elem-container">
                  <FormLabel>Target Mature Date</FormLabel>
                  <el-date-picker
                    v-model="formData.follow_up_date"
                    type="date"
                    value-format="YYYY-MM-DD"
                    class="datePickerClass"
                    placeholder="Please pick a target mature date"
                  />
                </div>
              </el-form-item>

              <el-form-item
                v-if="crmLeadType === 'b2b'"
                class="form-elem-container"
                prop="time_period"
              >
                <div class="form-elem-container">
                  <FormLabel>Time Period</FormLabel>
                  <el-select
                    v-model="formData.time_period"
                    class="w-full"
                    clearable
                    placeholder="Please select time period"
                  >
                    <el-option
                      v-for="item in timePeriodOptions"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value"
                    ></el-option>
                  </el-select>
                </div>
              </el-form-item>

              <el-form-item
                v-if="crmLeadType === 'b2b'"
                class="form-elem-container"
                prop="dr_number"
              >
                <div class="form-elem-container">
                  <FormLabel>DR Number</FormLabel>
                  <el-input
                    v-model="formData.dr_number"
                    type="number"
                    class="w-full"
                  />
                </div>
              </el-form-item>

              <el-form-item
                class="form-elem-container"
                prop="closing_business_value_pri"
              >
                <div class="form-elem-container">
                  <FormLabel
                    >Closing Business Value ({{
                      configStore.globalConfig.crm.primary_currency?.view
                    }})</FormLabel
                  >
                  <el-input
                    v-model="formData.closing_business_value_pri"
                    type="number"
                    class="w-full"
                    placeholder="Please input business value"
                  />
                </div>
              </el-form-item>

              <el-form-item
                v-if="
                  crmLeadType === 'b2b' &&
                  configStore.globalConfig.crm.secondary_currency
                "
                class="form-elem-container"
                prop="closing_business_value_sec"
              >
                <div class="form-elem-container">
                  <FormLabel
                    >Closing Business Value ({{
                      configStore.globalConfig.crm.secondary_currency?.view
                    }})</FormLabel
                  >
                  <el-input
                    v-model="formData.closing_business_value_sec"
                    type="number"
                    class="w-full"
                    placeholder="Please input business value"
                  />
                </div>
              </el-form-item>

              <el-form-item
                v-if="leadReferral"
                class="form-elem-container"
                prop="referral_id"
              >
                <div class="form-elem-container">
                  <FormLabel>Referral</FormLabel>
                  <el-input :model-value="leadReferralLabel" disabled />
                </div>
              </el-form-item>

              <el-form-item class="form-elem-container" prop="description">
                <div class="form-elem-container">
                  <FormLabel> Description</FormLabel>
                  <el-input
                    v-model="formData.description"
                    :rows="2"
                    :size="size"
                    type="textarea"
                    placeholder="Please input description"
                  />
                </div>
              </el-form-item>

              <!-- Saved as an ordinary lead comment, so it reads back under Lead
                   Comments on the lead page and notifies whoever is mentioned.
                   Hidden in bulk mode: one comment written here would be copied
                   onto every created lead, and every mention notified once per
                   lead. -->
              <el-form-item
                v-if="!isBulkFromProfiles"
                class="form-elem-container"
              >
                <div class="form-elem-container">
                  <FormLabel>Comment</FormLabel>
                  <CommentComposer ref="commentComposer" :show-send="false" />
                </div>
              </el-form-item>

              <el-form-item prop="attachments">
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col md:flex-row md:items-center gap-2">
                    <FormLabel>Attachments</FormLabel>
                    <div class="ml-2 mb-2">
                      <el-button
                        size="small"
                        plain
                        @click="
                          $refs.uploadRef?.$el.querySelector('input').click()
                        "
                      >
                        <DocumentAdd class="w-4 h-4" />
                        <p>Add Attachments</p>
                      </el-button>
                    </div>
                  </div>

                  <el-upload
                    ref="uploadRef"
                    v-model:file-list="fileList"
                    :auto-upload="false"
                    :multiple="true"
                    :show-file-list="false"
                    :on-preview="handlePreview"
                    :on-change="handleFileChange"
                    :accept="getAcceptValue()"
                    style="display: none"
                  />

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div
                      v-for="(file, index) in fileList"
                      :key="file.uid"
                      class="flex items-center gap-4 border p-2 rounded-md"
                    >
                      <div
                        class="w-16 h-16 flex items-center justify-center bg-gray-100 rounded"
                      >
                        <img
                          v-if="file.url || file.raw?.type.startsWith('image')"
                          :src="file.url"
                          class="w-full h-full object-cover rounded cursor-pointer"
                          @click="
                            () => {
                              previewIndex = index;
                              showViewer = true;
                            }
                          "
                        />
                        <el-icon v-else-if="file.raw?.type?.startsWith('audio')"
                          ><Headset
                        /></el-icon>
                        <el-icon v-else-if="file.raw?.type?.startsWith('video')"
                          ><VideoCamera
                        /></el-icon>
                        <el-icon
                          v-else-if="file.raw?.type === 'application/pdf'"
                          ><Document
                        /></el-icon>
                        <el-icon v-else><Folder /></el-icon>
                      </div>

                      <div class="flex flex-col gap-1">
                        <el-input
                          v-model="file.attachmentName"
                          placeholder="File name"
                          class="w-1/3"
                        />
                        <el-input
                          v-model="file.description"
                          placeholder="File description"
                          type="textarea"
                        />
                      </div>

                      <el-button circle size="small" @click="removeFile(index)">
                        <el-icon><Close /></el-icon>
                      </el-button>
                    </div>
                  </div>
                </div>
              </el-form-item>
              <div
                v-if="showFollowUpForm"
                class="mt-4 p-4 rounded border border-gray-200 space-y-4"
              >
                <div
                  v-for="(followUpTaskForm, index) in followUpTaskForms"
                  :id="`follow-up-item-${index}`"
                  :key="index"
                  class="space-y-4 p-3 rounded border border-gray-100"
                >
                  <div class="flex items-center justify-between">
                    <p class="text-sm font-semibold">
                      Follow Up {{ index + 1 }}
                    </p>
                    <button
                      class="text-xs text-red-500"
                      @click.prevent="removeFollowUp(index)"
                    >
                      Remove
                    </button>
                  </div>

                  <el-form-item class="form-elem-container" required>
                    <div class="form-elem-container">
                      <FormLabel :required="true">Title</FormLabel>
                      <el-input
                        v-model="followUpTaskForm.title"
                        placeholder="Enter follow up title"
                      />
                    </div>
                  </el-form-item>

                  <el-form-item class="form-elem-container" required>
                    <div class="form-elem-container">
                      <FormLabel :required="true">Agent</FormLabel>
                      <el-select
                        v-model="followUpTaskForm.agent_id"
                        placeholder="Select agent"
                        class="w-full"
                      >
                        <el-option
                          v-for="agent in statusFilteredAgents"
                          :key="agent.id"
                          :label="agent.name"
                          :value="agent.id"
                        />
                      </el-select>
                    </div>
                  </el-form-item>

                  <el-form-item class="" required>
                    <div class="form-elem-container">
                      <FormLabel :required="true"> Due Date & Time</FormLabel>
                      <el-date-picker
                        v-model="followUpTaskForm.follow_up_at"
                        placeholder="Select date & time"
                        type="datetime"
                        format="YYYY-MM-DD HH:mm:ss"
                        value-format="YYYY-MM-DD HH:mm:ss"
                        :shortcuts="shortcuts"
                        :disabled-date="disabledDate"
                        @change="handleDueDateChange"
                      />
                    </div>
                  </el-form-item>
                </div>
              </div>

              <div class="mt-4">
                <button
                  class="cursor-pointer text-light-purple underline-light-purple- underline text-xs w-full flex justify-end dark:text-light-purple-bar"
                  @click.prevent="openFollowUpForm"
                >
                  Add Follow Up
                </button>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- Additional Tab -->
        <el-tab-pane label="Additional" name="additional">
          <div class="grid grid-cols-2 gap-2">
            <el-form-item
              v-for="field of customFields"
              :key="field.slug"
              class="flex gap-3"
            >
              <div class="form-elem-container">
                <FormLabel :required="field.is_required">{{
                  field.label
                }}</FormLabel>
                <el-select
                  v-if="field.type === 'select'"
                  v-model="formData.custom_fields[field.slug]"
                  placeholder="Select"
                  filterable
                  clearable
                >
                  <el-option
                    v-for="item in field.options"
                    :key="item.id"
                    :label="item.label"
                    :value="item.id"
                  />
                </el-select>
                <el-select
                  v-else-if="field.type === 'multi_select'"
                  v-model="formData.custom_fields[field.slug]"
                  placeholder="Select"
                  multiple
                  :collapse-tags="true"
                  filterable
                  clearable
                >
                  <el-option
                    v-for="item in field.options"
                    :key="item.id"
                    :label="item.label"
                    :value="item.id"
                  />
                </el-select>
                <el-input
                  v-else-if="field.type === 'text'"
                  v-model="formData.custom_fields[field.slug]"
                  type="textarea"
                  :rows="2"
                />
                <el-input
                  v-else-if="field.type === 'string'"
                  v-model="formData.custom_fields[field.slug]"
                  type="text"
                />
                <el-input
                  v-else-if="field.type === 'int'"
                  v-model="formData.custom_fields[field.slug]"
                  type="number"
                />
                <el-input-number
                  v-else-if="field.type === 'decimal'"
                  v-model="formData.custom_fields[field.slug]"
                  :precision="2"
                  :step="0.01"
                  :controls="false"
                />
                <el-switch
                  v-else-if="field.type === 'boolean'"
                  v-model="formData.custom_fields[field.slug]"
                />
                <el-date-picker
                  v-else-if="field.type === 'date'"
                  v-model="formData.custom_fields[field.slug]"
                  placeholder="Pick a date"
                  type="date"
                  format="YYYY-MM-DD"
                  value-format="YYYY-MM-DD"
                />
                <el-date-picker
                  v-else-if="field.type === 'date_time'"
                  v-model="formData.custom_fields[field.slug]"
                  placeholder="Pick Date and Time"
                  type="datetime"
                  format="YYYY-MM-DD HH:mm:ss"
                  value-format="YYYY-MM-DD HH:mm:ss"
                />
              </div>
            </el-form-item>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
    <div class="feature-filter-submit">
      <NavButton v-if="isBulkFromProfiles" type="button" @click="goToPrevious">
        Previous
      </NavButton>
      <NavButton v-else type="button" @click="closeModal"> Cancel</NavButton>
      <NavButton
        class="bg-light-purple-nav"
        type="submit"
        :is-disabled="!isUserPermitted(PermissionNames.LEAD_CREATE)"
      >
        Create
      </NavButton>
    </div>
  </el-form>

  <!-- <CrmAttachmentForm
    :show-header="false"
    :show-attachment-modal="showAttachmentModal"
    :related-type="'lead'"
    @add-attachment="appendAttachment"
    @close-attachment-modal="closeAttachment"
  /> -->
</template>

<style setup>
.datePickerClass {
  width: 100% !important;
}
.custom-basic-tabs .el-tabs__item.is-active {
  color: white !important;
  background-color: #5a55ce !important;
}
</style>
